import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner,
  Input, Alert, Table, Badge,
} from "reactstrap";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

// Kolon sırası panelin geri kalanıyla aynı olsun diye sabit.
const HAK_ALANLARI = [
  { key: "ILAN_HAKKI", label: "İlan", color: "info" },
  { key: "MEZAT_HAKKI", label: "Mezat", color: "warning" },
  { key: "MEZAT_UZATMA_HAKKI", label: "Uzatma", color: "dark" },
  { key: "DEGERLEME_HAKKI", label: "Değerleme", color: "success" },
];

const BOS_SATIR = { ID: null, ILAN_HAKKI: 0, MEZAT_HAKKI: 0, MEZAT_UZATMA_HAKKI: 0, DEGERLEME_HAKKI: 0 };

/**
 * Kullanıcı listesinden tek tıkla açılan kategori-bazlı hak editörü.
 *
 * Kategori Bazlı Haklar sayfası kayıt başına tek satır düzenletiyor; burada
 * kullanıcının TÜM ana kategorileri tek ekranda gelir, sadece değişen satırlar
 * kaydedilir. Kaydı olmayan kategori için POST, olan için PATCH atılır (upsert)
 * — admin "önce kayıt oluştur" adımıyla uğraşmaz.
 */
const HizliHakModal = ({ isOpen, toggle, user, headers, onSaved }) => {
  const [kategoriler, setKategoriler] = useState([]);
  const [orijinal, setOrijinal] = useState({});   // { [kategoriId]: satır }
  const [taslak, setTaslak] = useState({});       // { [kategoriId]: satır }
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hata, setHata] = useState(null);

  // Toplu ekleme çubuğu
  const [topluAlan, setTopluAlan] = useState("MEZAT_HAKKI");
  const [topluMiktar, setTopluMiktar] = useState(1);

  const yukle = useCallback(async () => {
    if (!user?.ID) return;
    setIsLoading(true);
    setHata(null);
    try {
      const [resKat, resBag, resHak] = await Promise.all([
        fetch(`${BASE_URL}/KATEGORILER?$select=ID,AD&$orderby=SIRA_NO asc`, { headers }),
        fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI?$select=ALT_KATEGORI_ID&$top=10000`, { headers }),
        fetch(`${BASE_URL}/KULLANICI_KATEGORI_HAKLARI?$filter=KULLANICI_ID eq ${user.ID}&$top=2000`, { headers }),
      ]);
      const katJson = await resKat.json();
      const bagJson = await resBag.json();
      const hakJson = await resHak.json();

      // Haklar yalnızca ANA kategoride tutuluyor (backend ResolveAnaKategoriId
      // alt kategoriyi zaten en tepeye çeviriyor), alt kategorileri listeleme.
      const altIds = new Set((bagJson.value || []).map((b) => b.ALT_KATEGORI_ID));
      const anaKategoriler = (katJson.value || []).filter((k) => !altIds.has(k.ID));

      const mevcut = {};
      (hakJson.value || []).forEach((r) => {
        mevcut[r.KATEGORI_ID] = {
          ID: r.ID,
          ILAN_HAKKI: r.ILAN_HAKKI ?? 0,
          MEZAT_HAKKI: r.MEZAT_HAKKI ?? 0,
          MEZAT_UZATMA_HAKKI: r.MEZAT_UZATMA_HAKKI ?? 0,
          DEGERLEME_HAKKI: r.DEGERLEME_HAKKI ?? 0,
        };
      });

      const baslangic = {};
      anaKategoriler.forEach((k) => {
        baslangic[k.ID] = mevcut[k.ID] || { ...BOS_SATIR };
      });

      setKategoriler(anaKategoriler);
      setOrijinal(baslangic);
      setTaslak(JSON.parse(JSON.stringify(baslangic)));
    } catch {
      setHata("Kategoriler veya haklar yüklenemedi.");
    } finally {
      setIsLoading(false);
    }
  }, [user, headers]);

  useEffect(() => {
    if (isOpen) yukle();
  }, [isOpen, yukle]);

  const setDeger = (katId, alan, deger) => {
    const sayi = Math.max(0, Number.isFinite(Number(deger)) ? Math.trunc(Number(deger)) : 0);
    setTaslak((p) => ({ ...p, [katId]: { ...p[katId], [alan]: sayi } }));
  };

  const artir = (katId, alan, delta) => {
    setTaslak((p) => ({
      ...p,
      [katId]: { ...p[katId], [alan]: Math.max(0, (p[katId]?.[alan] || 0) + delta) },
    }));
  };

  const topluEkle = () => {
    const delta = Math.trunc(Number(topluMiktar) || 0);
    if (delta === 0) return;
    setTaslak((p) => {
      const yeni = { ...p };
      kategoriler.forEach((k) => {
        yeni[k.ID] = { ...yeni[k.ID], [topluAlan]: Math.max(0, (yeni[k.ID]?.[topluAlan] || 0) + delta) };
      });
      return yeni;
    });
  };

  // Sadece gerçekten değişen kategoriler kaydedilir — dokunulmamış satırlar için
  // gereksiz PATCH/POST atılmaz.
  const degisenler = useMemo(() => {
    return kategoriler.filter((k) => {
      const a = orijinal[k.ID], b = taslak[k.ID];
      if (!a || !b) return false;
      return HAK_ALANLARI.some((f) => a[f.key] !== b[f.key]);
    });
  }, [kategoriler, orijinal, taslak]);

  const kaydet = async () => {
    if (degisenler.length === 0) {
      toggle();
      return;
    }
    setIsSaving(true);
    setHata(null);
    try {
      const simdi = new Date().toISOString();
      const sonuclar = await Promise.all(
        degisenler.map((k) => {
          const satir = taslak[k.ID];
          const govde = {
            ILAN_HAKKI: satir.ILAN_HAKKI,
            MEZAT_HAKKI: satir.MEZAT_HAKKI,
            MEZAT_UZATMA_HAKKI: satir.MEZAT_UZATMA_HAKKI,
            DEGERLEME_HAKKI: satir.DEGERLEME_HAKKI,
            SON_GUNCELLEME: simdi,
          };
          if (satir.ID) {
            return fetch(`${BASE_URL}/KULLANICI_KATEGORI_HAKLARI(${satir.ID})`, {
              method: "PATCH", headers, body: JSON.stringify(govde),
            });
          }
          return fetch(`${BASE_URL}/KULLANICI_KATEGORI_HAKLARI`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              ...govde,
              KULLANICI_ID: user.ID,
              KATEGORI_ID: k.ID,
              OLUSTURMA_TARIHI: simdi,
            }),
          });
        })
      );

      const basarisiz = sonuclar.filter((r) => !r.ok);
      if (basarisiz.length > 0) {
        setHata(`${basarisiz.length} kategori kaydedilemedi. Yetkinizi kontrol edin.`);
        // Kısmi başarıyı ekrana yansıt — hangi satır geçti görülsün.
        await yukle();
        return;
      }

      if (onSaved) onSaved(degisenler.length);
      toggle();
    } catch {
      setHata("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" scrollable>
      <ModalHeader toggle={toggle}>
        <i className="mdi mdi-account-key-outline me-2 text-primary" />
        Hızlı Hak Ver — {user?.AD_SOYAD || user?.Mail_Adress}{" "}
        <small className="text-muted">(#{user?.ID})</small>
      </ModalHeader>

      <ModalBody>
        {hata && <Alert color="danger" toggle={() => setHata(null)}>{hata}</Alert>}

        {isLoading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : (
          <>
            <div className="d-flex align-items-center gap-2 flex-wrap mb-3 p-2 bg-light rounded">
              <span className="fw-bold small">Tüm kategorilere:</span>
              <Input
                type="select" bsSize="sm" style={{ width: 150 }}
                value={topluAlan} onChange={(e) => setTopluAlan(e.target.value)}
              >
                {HAK_ALANLARI.map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </Input>
              <Input
                type="number" bsSize="sm" style={{ width: 90 }}
                value={topluMiktar} onChange={(e) => setTopluMiktar(e.target.value)}
              />
              <Button color="primary" size="sm" onClick={topluEkle}>
                <i className="bx bx-plus me-1" />Ekle
              </Button>
              <span className="text-muted small ms-auto">
                {degisenler.length > 0
                  ? <Badge color="warning">{degisenler.length} kategori değişti</Badge>
                  : "Değişiklik yok"}
              </span>
            </div>

            <div className="table-responsive">
              <Table className="table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ minWidth: 180 }}>Kategori</th>
                    {HAK_ALANLARI.map((f) => (
                      <th key={f.key} className="text-center" style={{ minWidth: 160 }}>{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kategoriler.map((k) => {
                    const satir = taslak[k.ID] || BOS_SATIR;
                    const degisti = degisenler.some((d) => d.ID === k.ID);
                    return (
                      <tr key={k.ID} className={degisti ? "table-warning" : undefined}>
                        <td>
                          <span className="fw-medium">{k.AD}</span>
                          {!satir.ID && (
                            <Badge color="light" className="text-muted ms-2">yeni</Badge>
                          )}
                        </td>
                        {HAK_ALANLARI.map((f) => (
                          <td key={f.key}>
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <Button
                                color="light" size="sm" className="px-2"
                                onClick={() => artir(k.ID, f.key, -1)}
                                disabled={(satir[f.key] || 0) <= 0}
                              >−</Button>
                              <Input
                                type="number" bsSize="sm" min="0"
                                className="text-center px-1"
                                style={{ width: 62 }}
                                value={satir[f.key] ?? 0}
                                onChange={(e) => setDeger(k.ID, f.key, e.target.value)}
                              />
                              <Button
                                color="light" size="sm" className="px-2"
                                onClick={() => artir(k.ID, f.key, 1)}
                              >+</Button>
                              <Button
                                color="link" size="sm" className="px-1 text-primary"
                                onClick={() => artir(k.ID, f.key, 5)}
                                title="+5"
                              >+5</Button>
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" outline onClick={toggle} disabled={isSaving}>Kapat</Button>
        <Button color="primary" onClick={kaydet} disabled={isSaving || isLoading || degisenler.length === 0}>
          {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1" />}
          {degisenler.length > 0 ? `Kaydet (${degisenler.length})` : "Kaydet"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default HizliHakModal;
